import React from "react";
import { ReportCardSection } from "../../types";
import {
  ActiveSlice,
  ROSE_CENTER_X,
  ROSE_CENTER_Y,
  ROSE_R_CLIP,
  ROSE_R_INNER,
  ROSE_R_OUTER,
  ROSE_VIEWBOX,
  arcPath,
  bandArcRadii,
  sectorPath,
  sliceLayout,
  sliceRadius,
} from "./reportCardModel";

interface Props {
  /** Both the caption and the key the reveal animation staggers on. */
  label: string;
  kind: "conversation" | "solution";
  sections: ReportCardSection[];
  /** Where this kind's colours pick up, so no hue repeats across the two charts. */
  colorOffset: number;
  grade: number | null;
  bandName: string | null;
  /** Owned by the parent so the readout bar and both charts agree on one truth. */
  activeCriterionId: string | null;
  onActivate: (slice: ActiveSlice | null) => void;
  onSelect?: (criterionId: string) => void;
}

/**
 * Slice sweep is the criterion's share of its kind's weight; slice length out
 * from the hub is its grade on 0–100. Hue says which criterion, never how well
 * it did.
 *
 * Band thresholds are drawn as arcs across each slice's own wedge rather than
 * as rings around the whole chart, because performance levels are authored per
 * criterion: two criteria in one kind may set "Proficient" at different grades,
 * and a continuous ring would assert one criterion's thresholds over all of
 * them. Each slice's arcs are drawn last within its group, so they read over
 * the colour and travel with the slice when it lifts.
 */
const RoseChart: React.FC<Props> = ({
  label,
  kind,
  sections,
  colorOffset,
  grade,
  bandName,
  activeCriterionId,
  onActivate,
  onSelect,
}) => {
  const slices = sliceLayout(sections, colorOffset);
  const ownsActive = slices.some(
    (slice) => slice.section.criterionId === activeCriterionId,
  );
  const clipId = `rose-reveal-${kind}`;

  return (
    <figure className="rose" data-set={kind}>
      <figcaption>{label}</figcaption>
      <div className="rose-plot" data-active={ownsActive ? "" : undefined}>
        <svg
          viewBox={`0 0 ${ROSE_VIEWBOX} ${ROSE_VIEWBOX}`}
          role="group"
          aria-label={`${label} criteria: slice width is the criterion's weight, slice length is its grade`}
        >
          <defs>
            <clipPath id={clipId}>
              {/* Radial wipe for the entrance: the slices grow out of the hub.
                  Both ends of the wipe are handed to the keyframes as custom
                  properties, because a hardcoded end radius that falls short of
                  the geometry does not read as a shorter animation — it reads
                  as a chart whose outer bands and best grades are missing. */}
              <circle
                className="rose-reveal"
                cx={ROSE_CENTER_X}
                cy={ROSE_CENTER_Y}
                r={ROSE_R_CLIP}
                style={
                  {
                    "--r-hub": `${ROSE_R_INNER}px`,
                    "--r-clip": `${ROSE_R_CLIP}px`,
                  } as React.CSSProperties
                }
              />
            </clipPath>
          </defs>

          <g clipPath={`url(#${clipId})`}>
            {slices.map((slice) => {
              const { section, startAngle, endAngle, tx, ty, color } = slice;
              const isActive = section.criterionId === activeCriterionId;
              const full = sectorPath(
                ROSE_CENTER_X,
                ROSE_CENTER_Y,
                ROSE_R_INNER,
                ROSE_R_OUTER,
                startAngle,
                endAngle,
              );
              const earned = sectorPath(
                ROSE_CENTER_X,
                ROSE_CENTER_Y,
                ROSE_R_INNER,
                sliceRadius(section.grade),
                startAngle,
                endAngle,
              );
              const arcRadii = bandArcRadii(section.performanceLevels);
              const weightPercent = Math.round(slice.weight * 100);
              const activate = () =>
                onActivate({
                  criterionId: section.criterionId,
                  name: section.name,
                  grade: section.grade,
                  bandName: section.bandName,
                  weightPercent,
                });

              return (
                <g
                  key={section.criterionId}
                  className={`slice${isActive ? " is-active" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-label={
                    `${section.name}: ${Math.round(section.grade)} out of 100` +
                    (section.bandName ? `, ${section.bandName}` : "") +
                    `, weight ${weightPercent} percent. Activate to jump to the detail.`
                  }
                  style={
                    {
                      "--tx": `${tx.toFixed(2)}px`,
                      "--ty": `${ty.toFixed(2)}px`,
                    } as React.CSSProperties
                  }
                  onPointerEnter={activate}
                  onPointerLeave={() => onActivate(null)}
                  onFocus={activate}
                  onBlur={() => onActivate(null)}
                  onClick={() => onSelect?.(section.criterionId)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect?.(section.criterionId);
                    }
                  }}
                >
                  <g className="slice-shape">
                    {/* The unearned remainder, so the slice's full weight stays visible. */}
                    <path className="slice-ghost" d={full} style={{ fill: "var(--slice-rest)" }} />
                    <path className="slice-fill" d={earned} style={{ fill: color }} />
                    <path className="slice-outline" d={full} fill="none" />
                    {/* Full-wedge hit area: the unfilled remainder stays hoverable. */}
                    <path className="slice-hit" d={full} />
                    {/* This criterion's own thresholds, over its own wedge only.
                        The last radius is the 100 rim, which is what draws the
                        chart's outer circumference. */}
                    {arcRadii.map((r, i) => (
                      <path
                        key={i}
                        className={`band-arc${i === arcRadii.length - 1 ? " is-rim" : ""}`}
                        d={arcPath(
                          ROSE_CENTER_X,
                          ROSE_CENTER_Y,
                          r,
                          startAngle,
                          endAngle,
                        )}
                      />
                    ))}
                  </g>
                </g>
              );
            })}
          </g>
        </svg>

        <div className="rose-center">
          <span className="rose-score">{grade === null ? "—" : grade.toFixed(1)}</span>
          {bandName && <span className="rose-verdict">{bandName}</span>}
        </div>
      </div>
    </figure>
  );
};

export default RoseChart;
